/**
 * Payment API client — centralized fetch wrapper for the MIG payment service.
 * Replaces supabase.functions.invoke() calls for all payment-related endpoints.
 */

import { AI_BASE_URL } from '@/lib/ai-base-url';
import { getAuthToken, getCurrentUserId, isImpersonationSession } from '@/lib/auth-token';
import {
  ADS_OPT_OUT_UNCONFIRMED_CODE,
  OPT_OUT_MAX_ATTEMPTS,
  OPT_OUT_RETRY_BUDGET_MS,
  optOutBackoffMs,
  purchaseOptOutRequested,
} from '@/lib/privacy-opt-out';

const PAYMENT_BASE_URL = import.meta.env.VITE_PAYMENT_API_URL || AI_BASE_URL;

/**
 * How long any payment call may hang before it is abandoned.
 *
 * THIS BOUND IS THE ONLY FAILURE PATH SOME CALLERS HAVE. `fetch` has no default
 * timeout: a request to a wedged upstream stays pending until the tab is
 * closed, the promise never settles, and every `await paymentApi(...)` above it
 * never returns. The caller therefore never sets an error, never clears its
 * spinner, and never renders a retry — the page simply stops, with nothing in
 * any log (.claude/rules/silent-degradation.md). The two `/pay/start-trial`
 * callers are the acute case, because that POST is the moment a visitor who has
 * already signed up, verified, and signed back in converts.
 *
 * THE BOUND LIVES HERE, NOT IN THE CALLERS. Both start-trial callers
 * (useNextGenPracticeTrialSignup, useBarTrialPostAuthResolver) awaited this
 * function unguarded, and so does every other payment call in the app. Scoping
 * the fix to the concept — "no payment call may hang forever" — rather than to
 * the two files that surfaced it is .claude/rules/propagate-the-fix.md §2.
 *
 * 30s matches SUBMIT_TIMEOUT_MS in useNextGenPracticeTrialSignup, deliberately:
 * a visitor on this flow should never be waiting on two different unexplained
 * clocks.
 */
export const PAYMENT_TIMEOUT_MS = 30000;

interface PaymentApiOptions {
  /** Include Firebase JWT auth header. Default: true for authenticated routes. */
  auth?: boolean;
  /** HTTP method. Default: POST */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Abort after this many ms. Default: PAYMENT_TIMEOUT_MS. 0 disables. */
  timeoutMs?: number;
}

export interface PaymentApiResult<T = any> {
  data: T | null;
  error: string | null;
  errorData?: Record<string, any>;
  status?: number;
  /**
   * True ONLY when this client abandoned the request at `timeoutMs`.
   *
   * A timeout is reported as an ordinary `error` as well, so no caller has to
   * learn about this field to stop hanging: `classifyStartTrialResponse` sees a
   * non-null `error` with no `code` and no 409 status and returns
   * `{ kind: 'failed' }`, which both start-trial callers already turn into a
   * retryable refusal. The flag exists so a test can tell a timeout apart from
   * a 500 — an assertion on the message alone would pass against any network
   * error and could not distinguish a working bound from an absent one.
   */
  timedOut?: boolean;
}

/**
 * Requests that create something an ad platform could later be told about: a
 * PaymentIntent or SetupIntent, a subscription, a trial, a guest account.
 *
 * Each one carries `gpc: true` when the browser sends Global Privacy Control
 * (purchaseOptOutRequested), and the payment service commits the account's ads
 * opt-out BEFORE creating anything, or refuses with ADS_OPT_OUT_UNCONFIRMED_CODE
 * (services/payment/src/lib/ads-opt-out-gate.ts). Board qMSWo1t7k8otBnttEw9n:
 * before this, a guest checkout or a purchase/trial from a restored session
 * reported a GPC buyer.
 *
 * WHY HERE AND NOT AT THE CALL SITES: about twenty components post to these
 * paths. A per-site field is a fix that misses the next one
 * (.claude/rules/propagate-the-fix.md).
 *
 * /pay/guest/create is deliberately absent: it creates no account and nothing
 * that is reported, so there is nowhere to record the opt-out yet. The guest
 * signal rides /pay/guest/complete, which creates both.
 */
export const OPT_OUT_REQUEST_PATHS: ReadonlySet<string> = new Set([
  '/pay/create-payment',
  '/pay/activate',
  '/pay/start-trial',
  '/pay/guest/complete',
]);

const sleep = (ms: number) => new Promise<void>(resolve => { setTimeout(resolve, ms); });

function isOptOutUnconfirmed(result: PaymentApiResult): boolean {
  return result.error !== null && result.errorData?.code === ADS_OPT_OUT_UNCONFIRMED_CODE;
}

/**
 * Call a payment service endpoint.
 *
 * @param path - Route path (e.g., '/pay/create-payment')
 * @param body - Request body (omit for GET requests)
 * @param options - Auth, method, and timeout options
 * @returns Parsed JSON response
 */
export async function paymentApi<T = any>(
  path: string,
  body?: Record<string, any>,
  options: PaymentApiOptions = {}
): Promise<PaymentApiResult<T>> {
  const method = options.method ?? 'POST';
  if (method !== 'POST' || !OPT_OUT_REQUEST_PATHS.has(path)) {
    return paymentApiOnce<T>(path, body, options);
  }
  // Guest checkout creates a NEW account, so only this browser's live GPC
  // signal applies to it. A pending marker belongs to whichever account wrote
  // it, and an account that happens to be signed in here is not the one being
  // created (subscription-specialist, PR #98 review).
  const forNewAccount = path === '/pay/guest/complete';
  // Lambdas, not references: the session is read only when there is a signal.
  const optOut = await purchaseOptOutRequested({
    uid: () => (forNewAccount ? null : getCurrentUserId()),
    isImpersonation: () => isImpersonationSession(),
  });
  if (!optOut) return paymentApiOnce<T>(path, body, options);

  // Re-sent ONLY on the server's opt-out code, which it sends only when it has
  // created nothing. Same budget and backoff as PR #94's sign-in recorder: at
  // most OPT_OUT_MAX_ATTEMPTS attempts, never past OPT_OUT_RETRY_BUDGET_MS. A
  // timeout or any other failure is returned at once: those are not known to be
  // side-effect free.
  const sendBody = { ...(body ?? {}), gpc: true };
  const deadline = Date.now() + OPT_OUT_RETRY_BUDGET_MS;
  for (let attempt = 0; ; attempt++) {
    const result = await paymentApiOnce<T>(path, sendBody, options);
    if (!isOptOutUnconfirmed(result) || attempt + 1 >= OPT_OUT_MAX_ATTEMPTS) return result;
    const hinted = Number(result.errorData?.retry_after_seconds);
    const wait = Math.max(optOutBackoffMs(attempt), Number.isFinite(hinted) && hinted >= 0 ? hinted * 1000 : 0);
    if (Date.now() + wait >= deadline) return result;
    await sleep(wait);
  }
}

async function paymentApiOnce<T>(
  path: string,
  body: Record<string, any> | undefined,
  options: PaymentApiOptions
): Promise<PaymentApiResult<T>> {
  const { auth = true, method = 'POST', timeoutMs = PAYMENT_TIMEOUT_MS } = options;

  // Tracked separately from the AbortError itself. An abort surfaces as a
  // DOMException whose name and message vary by runtime (jsdom, Safari, and
  // Node do not agree), so branching on the error text would be a string
  // comparison that silently stops matching on a runtime bump.
  let timedOut = false;
  const controller = timeoutMs > 0 ? new AbortController() : null;
  const timer =
    controller && timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs)
      : null;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (auth) {
      const token = await getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${PAYMENT_BASE_URL}${path}`, {
      method,
      headers,
      ...(controller ? { signal: controller.signal } : {}),
      ...(body && method !== 'GET' ? { body: JSON.stringify(body) } : {}),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        data: null,
        error: data.error || data.message || `HTTP ${response.status}`,
        errorData: data,
        status: response.status,
      };
    }

    return { data, error: null, status: response.status };
  } catch (err) {
    if (timedOut) {
      return {
        data: null,
        error: `Request timed out after ${Math.round(timeoutMs / 1000)}s`,
        timedOut: true,
      };
    }
    return { data: null, error: err instanceof Error ? err.message : 'Network error' };
  } finally {
    // Cleared on EVERY exit, including the fast success path. A surviving timer
    // would fire `controller.abort()` on an already-settled request — harmless
    // today, but it also keeps a jsdom fake-timer test pending forever.
    if (timer !== null) clearTimeout(timer);
  }
}
